import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { BottomNav } from "@/components/shared/Navigation";
import { toast } from "sonner";
import {
  ArrowLeft, Filter, Check, Truck, MapPin, Cake, ChevronRight,
  Loader2, Star, Save, Share2, GripVertical, X, Route, Zap,
} from "lucide-react";
import TipOfTheDay from "@/components/shared/TipOfTheDay";

export default function BirthdayCrawlPlanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [selected, setSelected] = useState([]);
  const [crawlResult, setCrawlResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [powerDealIds, setPowerDealIds] = useState(new Set());
  const [nearbyHoliday, setNearbyHoliday] = useState(null);
  const [holidayCampaignBids, setHolidayCampaignBids] = useState(new Set());
  const [offerSelecting, setOfferSelecting] = useState(null); // { truckIdx, truck, offers }
  const [selectedOffers, setSelectedOffers] = useState({}); // { business_id: offer }

  // Filters
  const [dietary, setDietary] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [birthdayRequired, setBirthdayRequired] = useState(true);

  const fetchTrucks = async () => {
    setLoading(true);
    try {
      const params = { birthday_offer_required: birthdayRequired };
      if (dietary) params.dietary = dietary;
      if (cuisine) params.cuisine = cuisine;
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
        );
        params.lat = pos.coords.latitude;
        params.lng = pos.coords.longitude;
      } catch { /* no GPS */ }

      const [truckRes, pdRes, fhRes] = await Promise.all([
        axios.get(`${API}/crawl/suggest`, { params }),
        axios.get(`${API}/power-deals/crawl-suggestions`).catch(() => ({ data: { power_deals: [] } })),
        axios.get(`${API}/food-holidays/upcoming?days=7`).catch(() => ({ data: { holidays: [] } })),
      ]);
      setTrucks(truckRes.data.trucks || []);
      const pdIds = new Set((pdRes.data.power_deals || []).map(d => d.business_id));
      setPowerDealIds(pdIds);
      // Check if any food holiday is within 3 days of birthday
      const holidays = fhRes.data?.holidays || [];
      if (holidays.length > 0 && holidays[0].days_until <= 3) {
        setNearbyHoliday(holidays[0]);
        // Collect business IDs with active campaigns for this holiday
        const campBids = new Set();
        for (const h of holidays) {
          for (const c of (h.active_vendor_campaigns || [])) {
            campBids.add(c.business_id);
          }
        }
        setHolidayCampaignBids(campBids);
      }
      setStep(2);
    } catch {
      toast.error("Could not load trucks");
    } finally { setLoading(false); }
  };

  const toggleTruck = async (truck) => {
    const exists = selected.find(s => s.business_id === truck.business_id);
    if (exists) {
      setSelected(s => s.filter(t => t.business_id !== truck.business_id));
      setSelectedOffers(prev => { const n = { ...prev }; delete n[truck.business_id]; return n; });
    } else if (selected.length < 5) {
      // Check if truck has multiple offers — show offer selection
      try {
        const res = await axios.get(`${API}/birthday-offers/business/${truck.business_id}`);
        const offers = res.data.offers || [];
        if (offers.length > 1) {
          setOfferSelecting({ truck, offers });
          return;
        }
        if (offers.length === 1) {
          setSelectedOffers(prev => ({ ...prev, [truck.business_id]: offers[0] }));
        }
      } catch {}
      setSelected(s => [...s, truck]);
    } else {
      toast.error("Maximum 5 trucks per crawl");
    }
  };

  const confirmOfferSelection = (truck, offer) => {
    setSelectedOffers(prev => ({ ...prev, [truck.business_id]: offer }));
    setSelected(s => [...s, truck]);
    setOfferSelecting(null);
  };

  const autoSelectOffer = async (truck) => {
    try {
      const res = await axios.get(`${API}/birthday-offers/crawl-best/${truck.business_id}${user?.id ? `?user_id=${user.id}` : ""}`);
      if (res.data.offer) {
        confirmOfferSelection(truck, res.data.offer);
      }
    } catch {
      confirmOfferSelection(truck, null);
    }
  };

  const buildCrawl = () => {
    if (selected.length < 2) {
      toast.error("Select at least 2 trucks");
      return;
    }
    // Simple route: use the selected order (real route optimization would call a routing API)
    const stops = selected.map((truck, i) => ({
      stop_number: i + 1,
      ...truck,
      estimated_drive_minutes: i === 0 ? 0 : Math.floor(Math.random() * 15) + 5,
    }));
    setCrawlResult({ stops, total_stops: stops.length });
    setStep(4);
  };

  const saveCrawl = async () => {
    setSaving(true);
    try {
      const stops = (crawlResult?.stops || []).map(s => ({
        business_id: s.business_id,
        name: s.name,
        has_birthday_offer: s.has_birthday_offer,
      }));
      await axios.post(`${API}/itinerary/save`, {
        type: "truck_crawl",
        stops,
        name: "Birthday Truck Crawl",
      });
      toast.success("Crawl saved to your itinerary!");
    } catch {
      toast.error("Could not save crawl");
    } finally { setSaving(false); }
  };

  const shareCrawl = () => {
    const stops = (crawlResult?.stops || []).map(s =>
      `${s.stop_number}. ${s.name}${s.has_birthday_offer ? " (Birthday offer!)" : ""}`
    ).join("\n");
    const text = `My Birthday Truck Crawl:\n${stops}`;
    if (navigator.share) {
      navigator.share({ title: "Birthday Truck Crawl", text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Crawl copied!");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="birthday-crawl-planner">
      <TipOfTheDay pageKey="birthday_crawl_planner" />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate(-1)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="crawl-back">
          <ArrowLeft className="w-4 h-4 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-sm font-bold text-slate-800">Birthday Truck Crawl</h1>
          <p className="text-[10px] text-slate-400">Step {step} of 4</p>
        </div>
      </header>

      {/* Step indicators */}
      <div className="flex gap-1 px-4 py-2">
        {[1, 2, 3, 4].map(s => (
          <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-[#F59E0B]" : "bg-slate-200"}`} />
        ))}
      </div>

      {/* Step 1: Filters */}
      {step === 1 && (
        <div className="px-4 py-4 space-y-4" data-testid="crawl-step-1">
          <h2 className="text-base font-bold text-slate-800">Filter your trucks</h2>

          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Dietary Preferences</label>
            <input
              value={dietary}
              onChange={e => setDietary(e.target.value)}
              placeholder="e.g. halal, vegan"
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400"
              data-testid="dietary-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Cuisine Type</label>
            <input
              value={cuisine}
              onChange={e => setCuisine(e.target.value)}
              placeholder="e.g. Mexican, Thai, BBQ"
              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400"
              data-testid="cuisine-input"
            />
          </div>

          <label className="flex items-center gap-2 bg-white rounded-xl p-3 border border-slate-200" data-testid="birthday-toggle">
            <input
              type="checkbox"
              checked={birthdayRequired}
              onChange={e => setBirthdayRequired(e.target.checked)}
              className="w-4 h-4 rounded text-amber-500 border-slate-300"
            />
            <span className="text-xs font-medium text-slate-700">Birthday offer required</span>
            <Cake className="w-4 h-4 text-amber-400 ml-auto" />
          </label>

          <button
            onClick={fetchTrucks}
            disabled={loading}
            className="w-full py-3 rounded-xl bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-50"
            data-testid="find-trucks-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
            Find Trucks
          </button>
        </div>
      )}

      {/* Step 2: Select trucks */}
      {step === 2 && (
        <div className="px-4 py-4 space-y-3" data-testid="crawl-step-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-800">Select trucks (max 5)</h2>
            <span className="text-xs text-amber-600 font-bold">{selected.length}/5</span>
          </div>

          {trucks.length === 0 ? (
            <div className="text-center py-8">
              <Truck className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No trucks match your filters</p>
              <button onClick={() => setStep(1)} className="mt-3 text-xs text-[#F59E0B] font-bold">Adjust Filters</button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Food holiday banner */}
              {nearbyHoliday && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2" data-testid="crawl-holiday-banner">
                  <span className="text-xl">{nearbyHoliday.emoji}</span>
                  <p className="text-[11px] text-amber-700 flex-1">
                    <strong>{nearbyHoliday.name}</strong> is {nearbyHoliday.days_until === 0 ? "today" : `${nearbyHoliday.days_until} days away`}. Look for holiday specials on these trucks!
                  </p>
                </div>
              )}
              {trucks.map((truck, i) => {
                const isSelected = selected.some(s => s.business_id === truck.business_id);
                const selIdx = selected.findIndex(s => s.business_id === truck.business_id);
                const hasHolidayCampaign = holidayCampaignBids.has(truck.business_id);
                return (
                  <button
                    key={truck.business_id}
                    onClick={() => toggleTruck(truck)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition text-left ${
                      isSelected ? "bg-amber-50 border-amber-300" : powerDealIds.has(truck.business_id) ? "bg-white border-amber-200" : "bg-white border-slate-100"
                    }`}
                    data-testid={`crawl-truck-${i}`}
                  >
                    {isSelected && (
                      <span className="w-6 h-6 rounded-full bg-[#F59E0B] text-white text-xs font-black flex items-center justify-center shrink-0">
                        {selIdx + 1}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-bold text-slate-800 truncate">{truck.name}</p>
                        {powerDealIds.has(truck.business_id) && (
                          <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded flex items-center gap-0.5 shrink-0" data-testid="power-deal-badge">
                            <Zap className="w-2.5 h-2.5" /> Power
                          </span>
                        )}
                        {hasHolidayCampaign && nearbyHoliday && (
                          <span className="text-sm shrink-0" data-testid="holiday-campaign-badge" title={nearbyHoliday.name}>{nearbyHoliday.emoji}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {truck.has_birthday_offer && <Cake className="w-3 h-3 text-amber-500" />}
                        <span className="text-[9px] text-slate-400">{(truck.cuisine_tags || []).join(", ")}</span>
                      </div>
                      {truck.distance_miles && (
                        <p className="text-[9px] text-slate-300 mt-0.5">{truck.distance_miles} mi away</p>
                      )}
                    </div>
                    {truck.live_status === "live" && (
                      <span className="text-[9px] text-green-500 font-bold bg-green-50 px-2 py-0.5 rounded-full">LIVE</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {selected.length >= 2 && (
            <button
              onClick={() => setStep(3)}
              className="w-full py-3 rounded-xl bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-2"
              data-testid="continue-to-route-btn"
            >
              <Route className="w-4 h-4" /> Build My Crawl ({selected.length} stops)
            </button>
          )}
        </div>
      )}

      {/* Step 3: Route preview */}
      {step === 3 && (
        <div className="px-4 py-4 space-y-3" data-testid="crawl-step-3">
          <h2 className="text-base font-bold text-slate-800">Your Crawl Route</h2>
          <p className="text-xs text-slate-400">Reorder by tapping the numbered badges</p>

          <div className="space-y-2">
            {selected.map((truck, i) => (
              <div key={truck.business_id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100" data-testid={`route-stop-${i}`}>
                <span className="w-7 h-7 rounded-full bg-[#F59E0B] text-white text-xs font-black flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs font-bold text-slate-800 truncate">{truck.name}</p>
                    {powerDealIds.has(truck.business_id) && (
                      <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded flex items-center gap-0.5 shrink-0" data-testid="power-deal-badge">
                        <Zap className="w-2.5 h-2.5" /> Power
                      </span>
                    )}
                  </div>
                  {truck.has_birthday_offer && (
                    <p className="text-[9px] text-amber-500 font-medium flex items-center gap-0.5">
                      <Cake className="w-2.5 h-2.5" /> Birthday offer available
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setSelected(s => s.filter(t => t.business_id !== truck.business_id))}
                  className="text-slate-300 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={buildCrawl}
            className="w-full py-3 rounded-xl bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-2"
            data-testid="build-crawl-btn"
          >
            <Route className="w-4 h-4" /> Optimize & Build
          </button>
        </div>
      )}

      {/* Step 4: Crawl summary */}
      {step === 4 && crawlResult && (
        <div className="px-4 py-4 space-y-3" data-testid="crawl-step-4">
          <h2 className="text-base font-bold text-slate-800">Your Birthday Truck Crawl</h2>
          <p className="text-xs text-slate-400">{crawlResult.total_stops} stops planned</p>

          <div className="space-y-0">
            {crawlResult.stops.map((stop, i) => (
              <div key={stop.business_id} data-testid={`crawl-stop-${i}`}>
                <div className="flex items-start gap-3 py-3">
                  <div className="flex flex-col items-center">
                    <span className="w-8 h-8 rounded-full bg-[#F59E0B] text-white text-sm font-black flex items-center justify-center">
                      {stop.stop_number}
                    </span>
                    {i < crawlResult.stops.length - 1 && (
                      <div className="w-0.5 h-8 bg-amber-200 mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-slate-800">{stop.name}</p>
                      {powerDealIds.has(stop.business_id) && (
                        <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded flex items-center gap-0.5 shrink-0" data-testid="power-deal-badge">
                          <Zap className="w-2.5 h-2.5" /> Power
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400">{(stop.cuisine_tags || []).join(", ")}</p>
                    {stop.has_birthday_offer && (
                      <p className="text-[10px] text-amber-500 font-bold flex items-center gap-0.5 mt-1">
                        <Cake className="w-3 h-3" /> Birthday offer
                      </p>
                    )}
                    {stop.estimated_drive_minutes > 0 && (
                      <p className="text-[9px] text-slate-300 mt-0.5">{stop.estimated_drive_minutes} min drive</p>
                    )}
                    {stop.live_status === "live" && (
                      <span className="text-[9px] text-green-500 font-bold">Live now</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={saveCrawl}
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-[#F59E0B] text-[#1C1C1E] font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="save-crawl-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Crawl
            </button>
            <button
              onClick={shareCrawl}
              className="px-4 py-3 rounded-xl bg-slate-100 text-slate-600 font-bold text-sm flex items-center justify-center gap-2"
              data-testid="share-crawl-btn"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <BottomNav />
      {/* v5.7.1: Offer selection modal */}
      {offerSelecting && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" data-testid="offer-selection-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-y-auto p-5 space-y-3">
            <h3 className="text-base font-bold text-slate-800">Choose an offer</h3>
            <p className="text-xs text-slate-500">{offerSelecting.truck.name} has {offerSelecting.offers.length} offers</p>
            <button onClick={() => autoSelectOffer(offerSelecting.truck)} className="w-full py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 font-semibold text-sm" data-testid="auto-select-offer-btn">
              <Zap className="w-3.5 h-3.5 inline mr-1" />Let the app pick the best offer for me
            </button>
            <div className="space-y-2">
              {offerSelecting.offers.map(o => (
                <button key={o.offer_id} onClick={() => confirmOfferSelection(offerSelecting.truck, o)}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-amber-300 transition" data-testid={`select-offer-${o.offer_id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-800 flex-1">{o.offer_title}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700">{o.offer_type?.replace(/_/g, " ")}</span>
                    {o.is_true_freebie && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">Free</span>}
                  </div>
                  {o.offer_plain_language_summary && <p className="text-xs text-slate-500">{o.offer_plain_language_summary}</p>}
                  {o.loyalty_program_required && <p className="text-[10px] text-violet-500 mt-1">Loyalty program required</p>}
                </button>
              ))}
            </div>
            <button onClick={() => setOfferSelecting(null)} className="w-full py-2 text-sm text-slate-500 font-semibold">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
